import React, { useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import OrderStatusBadge, { orderStatusLabel } from "./OrderStatusBadge";
import TopBarProfile from "../../components/TopBarProfile/TopBarProfile";

import {
  Page,
  Inner,
  Title,
  Subtitle,
  EmptyBox,
  BackButton,
  TopFilters,
  FilterChip,
  OrdersGrid,
  OrderCard,
  OrderTop,
  OrderHeading,
  OrderNumber,
  OrderSubline,
  StatusRow,
  MetaGrid,
  MetaItem,
  SectionTitle,
  ProductsList,
  ProductRow,
  ProductImg,
  ProductInfo,
  ProductName,
  ProductMeta,
  ProductPrice,
  BottomGrid,
  Panel,
  Timeline,
  TimelineItem,
  NoteBox,
  ActionRow,
  ActionBtn,
  TotalRow,
  ClosePageButton,
  StatusSignal,
  DetailsToggle,
  DetailsWrap,
} from "./ComenziPageStyles";

export function paymentLabel(method, status) {
  if (method === "cash") {
    if (status === "platit") return "Plătită la ridicare";
    return "Neplătită";
  }

  if (method === "card") {
    if (status === "platit") return "Plătită online";
    if (status === "pending") return "Plată online în așteptare";
    if (status === "esuat") return "Plată online eșuată";
    if (status === "rambursat") return "Plată rambursată";
    return "Plată online";
  }

  return "—";
}

function getDisplayStatus(order) {
  if (order.payment_status === "esuat") {
    return {
      status: "plata_esuata",
      label: "Plată eșuată",
    };
  }

  return {
    status: order.status,
    label: orderStatusLabel(order.status),
  };
}

const GET_MY_ORDERS = gql`
  query GetMyOrders($email: String!) {
    myOrders(email: $email) {
      id
      order_number
      customer_nume
      customer_prenume
      customer_email
      customer_telefon
      informatii_suplimentare
      pickup_date
      pickup_interval
      pickup_location
      total
      status
      payment_status
      payment_method
      paid_at
      created_at
      updated_at
      internal_note
      items {
        id
        product_name
        product_image
        quantity
        unit_price
        line_total
        gramaj
        mesaj_selectat
        mentiuni
        personalizare_nume
        descriere_decor
        poza_model_url
      }
      history {
        id
        order_id
        status
        note
        created_at
      }
    }
  }
`;

const GET_ADMIN_ORDERS = gql`
  query GetAdminOrders {
    adminOrders {
      id
      order_number
      customer_nume
      customer_prenume
      customer_email
      customer_telefon
      informatii_suplimentare
      pickup_date
      pickup_interval
      pickup_location
      total
      status
      payment_status
      payment_method
      paid_at
      created_at
      updated_at
      internal_note
      items {
        id
        product_name
        product_image
        quantity
        unit_price
        line_total
        gramaj
        mesaj_selectat
        mentiuni
        personalizare_nume
        descriere_decor
        poza_model_url
      }
      history {
        id
        order_id
        status
        note
        created_at
      }
    }
  }
`;

const UPDATE_ORDER_STATUS = gql`
  mutation UpdateOrderStatus(
    $orderId: ID!
    $status: String!
    $note: String
    $payment_status: String
  ) {
    updateOrderStatus(
      orderId: $orderId
      status: $status
      note: $note
      payment_status: $payment_status
    ) {
      id
      status
      payment_status
      updated_at
      internal_note
      history {
        id
        order_id
        status
        note
        created_at
      }
    }
  }
`;

const UPDATE_ORDER_INTERNAL_NOTE = gql`
  mutation UpdateOrderInternalNote($orderId: ID!, $internal_note: String!) {
    updateOrderInternalNote(orderId: $orderId, internal_note: $internal_note) {
      id
      internal_note
      updated_at
    }
  }
`;

function roundPrice(value) {
  return Math.round(Number(value || 0) * 2) / 2;
}

function formatLei(value) {
  const rounded = roundPrice(value);

  return `${rounded.toLocaleString("ro-RO", {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })} lei`;
}

function formatRoDate(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatRoDateTime(dateStr) {
  if (!dateStr) return "—";

  let parsed;

  if (dateStr.includes("T")) {
    // deja ISO
    parsed = new Date(dateStr);
  } else {
    // format SQL → convertim
    parsed = new Date(dateStr.replace(" ", "T") + "Z");
  }

  if (isNaN(parsed)) return "—";

  return parsed.toLocaleString("ro-RO", {
    timeZone: "Europe/Bucharest",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function OrderProducts({ items = [] }) {
  return (
    <ProductsList>
      {items.map((item) => (
        <ProductRow key={item.id}>
          <ProductImg src={item.product_image} alt={item.product_name} />
          <ProductInfo>
            <ProductName>
              {item.product_name} × {item.quantity}
            </ProductName>

            {item.gramaj ? (
              <ProductMeta>Gramaj: {item.gramaj} g</ProductMeta>
            ) : null}
            {item.mesaj_selectat ? (
              <ProductMeta>Mesaj: {item.mesaj_selectat}</ProductMeta>
            ) : null}
            {item.personalizare_nume ? (
              <ProductMeta>
                Personalizare: {item.personalizare_nume}
              </ProductMeta>
            ) : null}
            {item.descriere_decor ? (
              <ProductMeta>Decor: {item.descriere_decor}</ProductMeta>
            ) : null}
            {item.mentiuni ? (
              <ProductMeta>Mențiuni: {item.mentiuni}</ProductMeta>
            ) : null}
          </ProductInfo>

          <ProductPrice>{formatLei(item.line_total)}</ProductPrice>
        </ProductRow>
      ))}
    </ProductsList>
  );
}

function UserOrdersView({ email }) {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_MY_ORDERS, {
    skip: !email,
    variables: { email },
    fetchPolicy: "network-only",
  });

  const [openOrders, setOpenOrders] = useState({});

  const toggleOrder = (id) => {
    setOpenOrders((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (!email) {
    return (
      <Page>
        <Inner>
          <Title>Comenzile mele</Title>
          <EmptyBox>
            Trebuie să fii autentificat pentru a vedea comenzile tale.
            <br />
            <br />
            <BackButton type="button" onClick={() => navigate("/login")}>
              Mergi la autentificare
            </BackButton>
          </EmptyBox>
        </Inner>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page>
        <Inner>
          <Title>Comenzile mele</Title>
          <Subtitle>Se încarcă comenzile tale...</Subtitle>
        </Inner>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Inner>
          <Title>Comenzile mele</Title>
          <EmptyBox>
            Nu am putut încărca istoricul comenzilor.
            <br />
            <br />
            <BackButton type="button" onClick={() => navigate("/")}>
              Înapoi la site
            </BackButton>
          </EmptyBox>
        </Inner>
      </Page>
    );
  }

  const orders = [...(data?.myOrders || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  if (!orders.length) {
    return (
      <Page>
        <Inner>
          <Title>Comenzile mele</Title>
          <Subtitle>
            Aici vor apărea toate comenzile tale plasate pe Ahava Cakes.
          </Subtitle>
          <EmptyBox>
            Nu ai încă nicio comandă.
            <br />
            <br />
            <BackButton type="button" onClick={() => navigate("/")}>
              Înapoi la magazin
            </BackButton>
          </EmptyBox>
        </Inner>
      </Page>
    );
  }

  return (
    <>
      <Page>
        <Inner>
          <Title>Comenzile mele</Title>
          <Subtitle>Istoricul complet al comenzilor tale.</Subtitle>

          <OrdersGrid>
            {orders.map((order) => {
              const isOpen = !!openOrders[order.id];

              const displayStatus = getDisplayStatus(order);
              return (
                <OrderCard key={order.id} $status={displayStatus.status}>
                  <OrderTop>
                    <OrderHeading>
                      <OrderNumber>{order.order_number}</OrderNumber>

                      <StatusSignal $status={displayStatus.status}>
                        {displayStatus.label}
                      </StatusSignal>

                      <OrderSubline>
                        Plasată la {formatRoDateTime(order.created_at)}
                      </OrderSubline>
                    </OrderHeading>

                    <StatusRow>
                      {order.payment_status !== "esuat" ? (
                        <OrderStatusBadge value={order.status} />
                      ) : null}
                      <OrderStatusBadge
                        type="payment"
                        value={order.payment_status}
                      />
                    </StatusRow>
                  </OrderTop>
                  <MetaGrid>
                    <MetaItem>
                      <strong>Data ridicării</strong>
                      <span>{formatRoDate(order.pickup_date)}</span>
                    </MetaItem>

                    <MetaItem>
                      <strong>Ora ridicării</strong>
                      <span>{order.pickup_interval || "—"}</span>
                    </MetaItem>

                    <MetaItem>
                      <strong>Loc ridicare</strong>
                      <span>{order.pickup_location || "—"}</span>
                    </MetaItem>

                    <MetaItem>
                      <strong>Plată</strong>
                      <span>
                        {paymentLabel(
                          order.payment_method,
                          order.payment_status,
                        )}
                      </span>
                    </MetaItem>
                  </MetaGrid>
                  <DetailsToggle onClick={() => toggleOrder(order.id)}>
                    {isOpen ? "Ascunde detalii ↑" : "Vezi detalii ↓"}
                  </DetailsToggle>
                  {isOpen && (
                    <DetailsWrap>
                      <SectionTitle>Produse</SectionTitle>
                      <OrderProducts items={order.items} />

                      <BottomGrid>
                        <Panel>
                          <SectionTitle>Istoric statusuri</SectionTitle>
                          <Timeline>
                            {(order.history || []).map((entry) => (
                              <TimelineItem key={entry.id}>
                                <strong>
                                  {orderStatusLabel(entry.status)}
                                </strong>
                                <span>
                                  {formatRoDateTime(entry.created_at)}
                                </span>
                                {entry.note ? <span>{entry.note}</span> : null}
                              </TimelineItem>
                            ))}
                          </Timeline>
                        </Panel>

                        <Panel>
                          <SectionTitle>Detalii comandă</SectionTitle>

                          <TimelineItem>
                            <strong>Nume client</strong>
                            <span>
                              {order.customer_nume} {order.customer_prenume}
                            </span>
                          </TimelineItem>

                          <TimelineItem style={{ marginTop: 10 }}>
                            <strong>Telefon</strong>
                            <span>{order.customer_telefon || "—"}</span>
                          </TimelineItem>

                          <TimelineItem style={{ marginTop: 10 }}>
                            <strong>Email</strong>
                            <span>{order.customer_email || "—"}</span>
                          </TimelineItem>

                          {order.informatii_suplimentare ? (
                            <TimelineItem style={{ marginBottom: 10 }}>
                              <strong>Informații suplimentare</strong>
                              <span>{order.informatii_suplimentare}</span>
                            </TimelineItem>
                          ) : null}

                          <TimelineItem>
                            <strong>Status curent</strong>
                            <span>{displayStatus.label}</span>
                          </TimelineItem>

                          <TimelineItem style={{ marginTop: 10 }}>
                            <strong>Ultima actualizare</strong>
                            <span>
                              {formatRoDateTime(
                                order.updated_at || order.created_at,
                              )}
                            </span>
                          </TimelineItem>

                          <TotalRow>
                            <strong>Total</strong>
                            <span>{formatLei(order.total)}</span>
                          </TotalRow>
                        </Panel>
                      </BottomGrid>
                    </DetailsWrap>
                  )}
                </OrderCard>
              );
            })}
          </OrdersGrid>
        </Inner>
      </Page>
    </>
  );
}

function AdminOrdersView() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("toate");
  const [draftNotes, setDraftNotes] = useState({});

  const { data, loading, error, refetch } = useQuery(GET_ADMIN_ORDERS, {
    fetchPolicy: "network-only",
  });

  const [openOrders, setOpenOrders] = useState({});

  const toggleOrder = (id) => {
    setOpenOrders((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const [updateOrderStatus, { loading: statusLoading }] = useMutation(
    UPDATE_ORDER_STATUS,
    {
      onCompleted: () => refetch(),
    },
  );

  const [updateOrderInternalNote, { loading: noteLoading }] = useMutation(
    UPDATE_ORDER_INTERNAL_NOTE,
    {
      onCompleted: () => refetch(),
    },
  );

  const orders = [...(data?.adminOrders || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  const filteredOrders = useMemo(() => {
    if (filter === "toate") return orders;
    return orders.filter((order) => order.status === filter);
  }, [orders, filter]);

  const handleStatus = async (orderId, status, payment_status) => {
    await updateOrderStatus({
      variables: {
        orderId,
        status,
        payment_status: payment_status || null,
      },
    });
  };

  const handleNoteSave = async (orderId) => {
    await updateOrderInternalNote({
      variables: {
        orderId,
        internal_note: draftNotes[orderId] || "",
      },
    });
  };

  if (loading) {
    return (
      <Page>
        <Inner>
          <Title>Dashboard comenzi</Title>
          <Subtitle>Se încarcă comenzile...</Subtitle>
        </Inner>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Inner>
          <Title>Dashboard comenzi</Title>
          <EmptyBox>Nu am putut încărca comenzile.</EmptyBox>
        </Inner>
      </Page>
    );
  }

  return (
    <Page>
      <ClosePageButton type="button" onClick={() => navigate("/")}>
        ×
      </ClosePageButton>
      <Inner>
        <Title>Dashboard comenzi</Title>
        <Subtitle>
          Vezi comenzile active și cele finalizate recent din ultimele 30 de
          zile.
        </Subtitle>

        <TopFilters>
          {[
            { id: "toate", label: "Toate" },
            { id: "confirmata", label: "Confirmate" },
            { id: "in_lucru", label: "În lucru" },
            { id: "gata", label: "Gata" },
            { id: "ridicata", label: "Ridicate" },
            { id: "livrata", label: "Livrate" },
            { id: "anulata", label: "Anulate" },
          ].map((item) => (
            <FilterChip
              key={item.id}
              type="button"
              $active={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </FilterChip>
          ))}
        </TopFilters>

        <OrdersGrid>
          {filteredOrders.map((order) => {
            const isOpen = !!openOrders[order.id];
            const displayStatus = getDisplayStatus(order);

            return (
              <OrderCard key={order.id} $status={displayStatus.status}>
                <OrderTop>
                  <OrderHeading>
                    <OrderNumber>{order.order_number}</OrderNumber>

                    <StatusSignal $status={displayStatus.status}>
                      {displayStatus.label}
                    </StatusSignal>

                    <OrderSubline>
                      {order.customer_nume} {order.customer_prenume} ·{" "}
                      {order.customer_telefon} · {order.customer_email}
                    </OrderSubline>
                  </OrderHeading>

                  <StatusRow>
                    {order.payment_status !== "esuat" ? (
                      <OrderStatusBadge value={order.status} />
                    ) : null}
                    <OrderStatusBadge
                      type="payment"
                      value={order.payment_status}
                    />
                  </StatusRow>
                </OrderTop>
                <MetaGrid>
                  <MetaItem>
                    <strong>Data ridicării</strong>
                    <span>{formatRoDate(order.pickup_date)}</span>
                  </MetaItem>

                  <MetaItem>
                    <strong>Ora ridicării</strong>
                    <span>{order.pickup_interval || "—"}</span>
                  </MetaItem>

                  <MetaItem>
                    <strong>Loc ridicare</strong>
                    <span>{order.pickup_location || "—"}</span>
                  </MetaItem>

                  <MetaItem>
                    <strong>Total</strong>
                    <span>{formatLei(order.total)}</span>
                  </MetaItem>
                </MetaGrid>
                <DetailsToggle onClick={() => toggleOrder(order.id)}>
                  {isOpen ? "Ascunde detalii ↑" : "Vezi detalii ↓"}
                </DetailsToggle>
                {isOpen && (
                  <DetailsWrap>
                    <SectionTitle>Produse</SectionTitle>
                    <OrderProducts items={order.items} />
                    <BottomGrid>
                      <Panel>
                        <SectionTitle>Acțiuni rapide</SectionTitle>

                        <ActionRow>
                          <ActionBtn
                            type="button"
                            onClick={() => handleStatus(order.id, "confirmata")}
                            disabled={statusLoading}
                          >
                            Confirmată
                          </ActionBtn>

                          <ActionBtn
                            type="button"
                            onClick={() => handleStatus(order.id, "in_lucru")}
                            disabled={statusLoading}
                          >
                            În lucru
                          </ActionBtn>

                          <ActionBtn
                            type="button"
                            onClick={() => handleStatus(order.id, "gata")}
                            disabled={statusLoading}
                          >
                            Gata
                          </ActionBtn>

                          <ActionBtn
                            type="button"
                            onClick={() =>
                              handleStatus(
                                order.id,
                                "ridicata",
                                order.payment_method === "cash"
                                  ? "platit"
                                  : null,
                              )
                            }
                            disabled={statusLoading}
                          >
                            Ridicată
                          </ActionBtn>

                          <ActionBtn
                            type="button"
                            onClick={() => handleStatus(order.id, "livrata")}
                            disabled={statusLoading}
                          >
                            Livrată
                          </ActionBtn>

                          <ActionBtn
                            type="button"
                            onClick={() => handleStatus(order.id, "anulata")}
                            disabled={statusLoading}
                          >
                            Anulată
                          </ActionBtn>
                        </ActionRow>

                        {/* {order.payment_method === "cash" ? (
                    <ActionRow>
                      <ActionBtn
                        type="button"
                        onClick={() =>
                          handleStatus(order.id, order.status, "platit")
                        }
                        disabled={statusLoading}
                      >
                        Marchează plătită
                      </ActionBtn>

                      <ActionBtn
                        type="button"
                        onClick={() =>
                          handleStatus(order.id, order.status, "neplatit")
                        }
                        disabled={statusLoading}
                      >
                        Marchează neplătită
                      </ActionBtn>
                    </ActionRow>
                  ) : null} */}

                        <SectionTitle style={{ marginTop: 18 }}>
                          Notă internă
                        </SectionTitle>
                        <NoteBox
                          value={
                            draftNotes[order.id] ?? order.internal_note ?? ""
                          }
                          onChange={(e) =>
                            setDraftNotes((prev) => ({
                              ...prev,
                              [order.id]: e.target.value,
                            }))
                          }
                          placeholder="Ex: tort delicat, client vine la 17:30, confirmat telefonic..."
                        />

                        <ActionRow>
                          <ActionBtn
                            type="button"
                            $dark
                            onClick={() => handleNoteSave(order.id)}
                            disabled={noteLoading}
                          >
                            Salvează nota
                          </ActionBtn>
                        </ActionRow>
                      </Panel>

                      <Panel>
                        <SectionTitle>Istoric statusuri</SectionTitle>

                        <Timeline>
                          {(order.history || []).map((entry) => (
                            <TimelineItem key={entry.id}>
                              <strong>{orderStatusLabel(entry.status)}</strong>
                              <span>{formatRoDateTime(entry.created_at)}</span>
                              {entry.note ? <span>{entry.note}</span> : null}
                            </TimelineItem>
                          ))}
                        </Timeline>

                        <SectionTitle style={{ marginTop: 12 }}>
                          Date client
                        </SectionTitle>

                        <TimelineItem>
                          <strong>Nume</strong>
                          <span>
                            {order.customer_nume} {order.customer_prenume}
                          </span>
                        </TimelineItem>

                        <TimelineItem style={{ marginTop: 8 }}>
                          <strong>Telefon</strong>
                          <span>{order.customer_telefon || "—"}</span>
                        </TimelineItem>

                        <TimelineItem style={{ marginTop: 8 }}>
                          <strong>Email</strong>
                          <span>{order.customer_email || "—"}</span>
                        </TimelineItem>
                        {order.informatii_suplimentare ? (
                          <TimelineItem style={{ marginTop: 12 }}>
                            <strong>Informații suplimentare client</strong>
                            <span>{order.informatii_suplimentare}</span>
                          </TimelineItem>
                        ) : null}

                        <TotalRow>
                          <strong>Total</strong>
                          <span>{formatLei(order.total)}</span>
                        </TotalRow>
                      </Panel>
                    </BottomGrid>
                  </DetailsWrap>
                )}
              </OrderCard>
            );
          })}
        </OrdersGrid>
      </Inner>
    </Page>
  );
}

export default function ComenziPage() {
  const navigate = useNavigate();

  let authUser = null;

  try {
    const authRaw = localStorage.getItem("user");
    authUser = authRaw ? JSON.parse(authRaw) : null;
  } catch {
    authUser = null;
  }

  const authEmail = authUser?.email || "";
  const role = authUser?.role || null;
  const isSimpleUser = role === "utilizator";

  if (!authUser) {
    return (
      <Page>
        <Inner>
          <Title>Comenzi</Title>
          <EmptyBox>
            Trebuie să fii autentificat pentru a accesa această pagină.
            <br />
            <br />
            <BackButton type="button" onClick={() => navigate("/login")}>
              Mergi la autentificare
            </BackButton>
          </EmptyBox>
        </Inner>
      </Page>
    );
  }

  if (role === "admin" || role === "angajat") {
    return <AdminOrdersView />;
  }

  return (
    <>
      <TopBarProfile activeTab="profile" />
      <UserOrdersView email={authEmail} />
    </>
  );
}
